import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/logo";
import { LegalFooter } from "@/components/marketing/legal-page";
import { cn } from "@/lib/utils";
import rankingData from "@/data/ranking_pages.json";
import { STAGE_LABEL, displayName, listTitle } from "@/lib/ranking-display-names";

const IMAGE = "https://capitaldart.com/capitaldart-og.png";

const STAGE_ORDER = ["Seed", "Early Stage", "Later Stage", "PE"] as const;

const DIMENSION_SECTIONS = [
  { dimension: "Company Type", title: "By Company Type" },
  { dimension: "Vertical", title: "By Vertical" },
] as const;

export const Route = createFileRoute("/lists/")({
  head: () => {
    const title = "Top 10 Investor Rankings by Category and Stage | CapitalDart";
    const description =
      "Top 10 investor rankings for 80+ company types and verticals across Seed, Early Stage, Later Stage, and Private Equity, ranked from CapitalDart deal data.";
    const url = "https://capitaldart.com/lists";

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: ListsIndexPage,
});

type RankingPage = (typeof rankingData.pages)[number];

type CategoryGroup = {
  category: string;
  pages: RankingPage[];
};

type Section = {
  title: string;
  groups: CategoryGroup[];
};

function groupPages(dimension: string): CategoryGroup[] {
  const byCategory = new Map<string, RankingPage[]>();

  for (const page of rankingData.pages) {
    if (page.dimension !== dimension) continue;
    const list = byCategory.get(page.category) ?? [];
    list.push(page);
    byCategory.set(page.category, list);
  }

  const groups: CategoryGroup[] = [];
  for (const [category, pages] of byCategory) {
    const ordered = pages.slice().sort((a, b) => {
      const ai = STAGE_ORDER.indexOf(a.stage as (typeof STAGE_ORDER)[number]);
      const bi = STAGE_ORDER.indexOf(b.stage as (typeof STAGE_ORDER)[number]);
      return ai - bi;
    });
    groups.push({ category, pages: ordered });
  }

  return groups.sort((a, b) => a.category.localeCompare(b.category));
}

const SECTIONS: Section[] = DIMENSION_SECTIONS.map(({ dimension, title }) => ({
  title,
  groups: groupPages(dimension),
}));

const ALL_CATEGORIES = SECTIONS.flatMap((section) =>
  section.groups.map((group) => ({
    category: group.category,
    sectionTitle: section.title,
  })),
);

function ListsIndexPage() {
  const [query, setQuery] = useState("");
  const [lockedCategory, setLockedCategory] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const normalizedQuery = query.trim().toLowerCase();

  const suggestions = useMemo(() => {
    if (!normalizedQuery) return [];
    return ALL_CATEGORIES.filter((c) =>
      (c.category + " " + displayName(c.category)).toLowerCase().includes(normalizedQuery),
    ).slice(0, 10);
  }, [normalizedQuery]);

  const totalMatches = useMemo(() => {
    if (!normalizedQuery) return 0;
    return ALL_CATEGORIES.filter((c) =>
      (c.category + " " + displayName(c.category)).toLowerCase().includes(normalizedQuery),
    ).length;
  }, [normalizedQuery]);

  const filteredSections = useMemo(() => {
    if (lockedCategory) {
      return SECTIONS.map((section) => ({
        ...section,
        groups: section.groups.filter((group) => group.category === lockedCategory),
      })).filter((section) => section.groups.length > 0);
    }
    if (!normalizedQuery) return SECTIONS;

    return SECTIONS.map((section) => ({
      ...section,
      groups: section.groups.filter((group) =>
        (group.category + " " + displayName(group.category)).toLowerCase().includes(normalizedQuery),
      ),
    })).filter((section) => section.groups.length > 0);
  }, [lockedCategory, normalizedQuery]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (open && suggestions.length > 0 && (activeIndex == null || activeIndex >= suggestions.length)) {
      setActiveIndex(0);
    }
  }, [open, suggestions, activeIndex]);

  const selectCategory = (category: string) => {
    setQuery(category);
    setLockedCategory(category);
    setOpen(false);
    setActiveIndex(null);
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
    setLockedCategory(null);
    if (value.trim().length > 0) {
      setOpen(true);
      setActiveIndex(0);
    } else {
      setOpen(false);
      setActiveIndex(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && suggestions.length > 0 && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(e.key === "ArrowDown" ? 0 : suggestions.length - 1);
      return;
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => {
        if (i == null) return 0;
        return i >= suggestions.length - 1 ? 0 : i + 1;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => {
        if (i == null) return suggestions.length - 1;
        return i <= 0 ? suggestions.length - 1 : i - 1;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = activeIndex ?? (suggestions.length > 0 ? 0 : null);
      if (idx != null && suggestions[idx]) {
        selectCategory(suggestions[idx].category);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setActiveIndex(null);
    }
  };

  const countText =
    totalMatches === 0
      ? "No matching categories."
      : `${totalMatches} ${totalMatches === 1 ? "category" : "categories"} match${totalMatches === 1 ? "es" : ""}`;

  const activeId =
    open && activeIndex != null && activeIndex < suggestions.length
      ? `suggestion-${activeIndex}`
      : undefined;

  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-12 sm:py-16">
      <article className="relative mx-auto max-w-[900px] overflow-hidden rounded-2xl border border-brand-line bg-brand-paper shadow-[0_1px_0_var(--brand-line),0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.18)]">
        <nav className="flex items-center justify-between border-b border-brand-line px-[22px] py-4 lg:px-10">
          <Link to="/" className="flex items-center gap-2 text-brand-ink no-underline">
            <Logo
              src="/capitaldart-logo.png"
              alt="CapitalDart"
              className="h-9 w-auto"
              height={36}
              trademarkClassName="text-brand-ink"
            />
          </Link>
          <Link
            to="/"
            className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
          >
            ← Back to capitaldart.com
          </Link>
        </nav>

        <div className="px-[22px] pt-9 pb-10 lg:px-10 lg:pt-12 lg:pb-14">
          <div className="font-mono text-[14px] uppercase tracking-[0.18em] text-brand-accent">
            Rankings
          </div>
          <h1 className="mt-3 font-serif text-[34px] leading-[1.1] text-brand-ink lg:text-[44px]">
            Top 10 Investor Rankings
          </h1>
          <p className="mt-4 max-w-[760px] text-[16px] leading-[1.55] text-brand-ink-2 lg:text-[18px]">
            Ranked from CapitalDart deal data across US and Canada companies, by category and stage.
          </p>

          <div ref={containerRef} className="relative mt-8 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted" />
            <Input
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              aria-controls="category-suggestions-list"
              aria-activedescendant={activeId}
              placeholder="Filter categories..."
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (!lockedCategory && query.trim().length > 0) {
                  setOpen(true);
                }
              }}
              className="border-brand-line bg-brand-paper pl-9 text-brand-ink placeholder:text-brand-muted focus-visible:ring-brand-accent"
              aria-label="Filter ranking categories"
            />

            {open && query.trim().length > 0 && (
              <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-brand-line bg-brand-paper shadow-md">
                <div
                  className="border-b border-brand-line px-3 py-2 text-[12px] text-brand-muted"
                  aria-live="polite"
                >
                  {countText}
                </div>
                {suggestions.length > 0 && (
                  <ul
                    id="category-suggestions-list"
                    role="listbox"
                    aria-label="Category suggestions"
                    className="max-h-60 overflow-auto py-1"
                  >
                    {suggestions.map((suggestion, i) => (
                      <li
                        key={`${suggestion.sectionTitle}-${suggestion.category}`}
                        id={`suggestion-${i}`}
                        role="option"
                        aria-selected={activeIndex === i}
                        onClick={() => selectCategory(suggestion.category)}
                        className={cn(
                          "cursor-pointer px-3 py-2 text-[14px] text-brand-ink",
                          activeIndex === i ? "bg-brand-accent/10" : "hover:bg-brand-paper-2",
                        )}
                      >
                        <Highlight text={suggestion.category} query={query} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {filteredSections.length === 0 && (
            <p className="mt-12 text-[15px] text-brand-muted">
              No categories match your search.
            </p>
          )}

          {filteredSections.map((section) => (
            <section key={section.title} className="mt-12">
              <h2 className="font-serif text-[24px] leading-[1.2] text-brand-ink lg:text-[28px]">
                {section.title}
              </h2>

              <div className="mt-6 grid gap-3">
                {section.groups.map((group) => (
                  <div
                    key={group.category}
                    className="flex flex-col gap-3 rounded-lg border border-brand-line bg-brand-paper-2 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-[200px] text-[15px] font-medium text-brand-ink">
                      {displayName(group.category)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[13.5px]">
                      {group.pages.map((page) => (
                        <Link
                          key={page.slug}
                          to="/lists/$slug"
                          params={{ slug: page.slug }}
                          title={listTitle(page.stage, page.category)}
                          aria-label={listTitle(page.stage, page.category)}
                          className="rounded-md border border-brand-line bg-brand-paper px-3 py-1.5 text-brand-ink no-underline transition-colors hover:border-brand-accent hover:text-brand-accent"
                        >
                          {STAGE_LABEL[page.stage] ?? page.stage}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        <LegalFooter />
      </article>
    </main>
  );
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${safe})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-brand-accent/20 font-medium text-brand-ink">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
